<template>
  <div>
    <link rel="stylesheet" href="https://use.fontawesome.com/releases/v5.8.1/css/all.css" integrity="sha384-50oBUHEmvpQ+1lW4y57PTFmhCaXp0ML5d60M1M7uH2+nqUivzIebhndOJK28anvf" crossorigin="anonymous">
    <div class="card" v-if="product">
      <div class="card__image">
        <div class="card__image__inside">
          <img :src="product.image" :alt="product.name" class="w-100" />
        </div>
      </div>
      <div class="card__info"
           :class="{ 'bg-success': isProductPlentyInStock(product),
                   'bg-danger': !isProductInStock(product),
                   'bg-warning': isProductInStock(product) && !isProductPlentyInStock(product) }"
           @click="navigateToProject(project)">
        <div v-if="product.preorder" class="d-flex justify-content-center">
          <div>{{ $t('messages.preorder') }}</div>
          <div class="position-relative">
            <font-awesome-icon icon="circle" class="text-white position-button"></font-awesome-icon>
            <b-button v-b-tooltip.hover :title="$t('messages.preorder_info')" class="ml-1 mr-1 noPadding noBackground position-relative">
              <font-awesome-icon icon="info-circle" class="red"></font-awesome-icon>
            </b-button>
          </div>
        </div>
        <div v-if="product.backorder" class="d-flex justify-content-center">
          <div>{{ $t('messages.backorder') }}</div>
          <div class="position-relative">
            <font-awesome-icon icon="circle" class="text-white position-button"></font-awesome-icon>
            <b-button v-b-tooltip.hover :title="$t('messages.backorder_info')" class="ml-1 mr-1 noPadding noBackground position-relative">
              <font-awesome-icon icon="info-circle" class="red"></font-awesome-icon>
            </b-button>
          </div>
        </div>
        <div v-if="!product.preorder && !product.backorder">
          <div v-if="isProductPlentyInStock(product)">
            {{ $t('messages.stock_in') }}
          </div>
          <div v-if="!isProductInStock(product)">
            {{ $t('messages.sold_out') }}
          </div>
          <div v-if="isProductInStock(product) && !isProductPlentyInStock(product)">
            {{ $t('messages.stock_almost_sold_out') }}
          </div>
        </div>
      </div>
      <h1 class="card__title d-flex">
        <div class="full-width">{{ product.name }}</div>
        <div v-if="loggedIn && isAdmin">
          <b-button v-if="!editing" v-b-tooltip.hover :title="$t('messages.manage')" variant="primary" @click="$emit('startEditing', true)">
            <font-awesome-icon icon="pencil-alt"></font-awesome-icon>
          </b-button>
          <b-button v-if="editing" v-b-tooltip.hover :title="$t('messages.back_to_product')" variant="primary" @click="$emit('startEditing', false)">
            <font-awesome-icon icon="arrow-left"></font-awesome-icon>
          </b-button>
        </div>
      </h1>
      <div class="card__language">
        <div class="flag-size" v-if="!editing">
          <flag v-for="language in product.language" :key="'flag-' + language" :iso="getFlagLanguageString(language)" :squared="false" class="mr-2"></flag>
        </div>
        <b-form-group id="languageGroup"
                      v-if="editing"
                      :label="$t('messages.language')+':'">
          <b-form-checkbox v-for="language in languages" :key="'lang-' + language" :value="language"
                           v-model="product.language">
            {{ $t('languages.' + language) }}
          </b-form-checkbox>
        </b-form-group>
      </div>
      <div class="card__description border-0 pb-2">
        <b-form-group id="eanGroup"
                      v-if="editing"
                      :label="$t('messages.ean')+':'"
                      label-for="ean">
          <b-form-input id="ean" name="ean"
                        v-model="product.ean"></b-form-input>
        </b-form-group>
        <p v-if="!editing" v-html="product.description"></p>
        <b-form-group id="productRangeGroup"
                      v-if="editing"
                      :label="$t('messages.product_range')+':'"
                      label-for="productRange">
          <b-form-checkbox id="productRange" name="productRange" class="product-range-checkbox-fix"
                           v-model="product.product_range"></b-form-checkbox>
        </b-form-group>
        <b-form-group id="priceGroup"
                      v-if="editing"
                      :label="$t('messages.price')+' (€):'"
                      label-for="price"
                      :invalid-feedback="!$v.product.price.decimalTwoRounds ? $t('errors.price_valid') : $t('errors.price')">
          <b-form-input id="price" name="price"
                        min="0" :number="true" type="number" step="0.01"
                        v-model="product.price"
                        @input="$v.product.price.$touch"
                        :state="$v.product.price.$dirty ? !$v.product.price.$error : null"></b-form-input>
        </b-form-group>
        <b-form-group id="reducedPriceGroup"
                      v-if="editing"
                      :label="$t('messages.reduced_price')+' (€):'"
                      label-for="reducedPrice"
                      :invalid-feedback="!$v.product.reduced_price.decimalTwoRounds ? $t('errors.reduced_price_valid') : $t('errors.reduced_price')">
          <b-form-input id="reducedPrice" name="reducedPrice"
                        min="0" :number="true" type="number" step="0.01"
                        v-model="product.reduced_price"
                        @input="$v.product.reduced_price.$touch"
                        :state="$v.product.reduced_price.$dirty ? !$v.product.reduced_price.$error : null"></b-form-input>
        </b-form-group>
        <ckeditor v-if="editing" id="productContent" name="productContent" :editor="editor.instance" v-model="editor.data"
                  :config="editor.config" :key="editor.update"></ckeditor>
      </div>
      <div class="pl-20 pr-20 pt-3 pb-2 strong" v-if="nextDeliveryDay && showOrderFast && !editing && product.stock > 0">
        {{ $t('messages.shipping_tomorrow_product') }}
      </div>
      <div class="pl-20 pr-20 pt-3 pb-2 d-flex align-items-center" v-if="!editing">
        <div class="w-100">
          <p>
            <span class="red strong mr-2" v-if="product.old_price"><del>€ {{ product.old_price | round(2) }}</del></span>
            <span class="green strong">€ {{ product.price | round(2) }}</span>
          </p>
        </div>
        <div class="d-flex justify-content-end" v-if="isProductInStock(product)">
          <div class="mr-2">
            <b-form-group id="totalGroup" class="m-0"
                          label-for="total">
              <b-form-input id="total" name="total" class="total-width"
                            min="1" :number="true" type="number"
                            v-model="total"></b-form-input>
            </b-form-group>
          </div>
          <div>
            <cart-button :product="product" :amount="total" :disabled="!total" />
          </div>
        </div>
      </div>
      <div class="pl-20 pr-20" :class="{ 'pt-3': editing }">
        <b-form-group id="orderedGroup"
                      v-if="editing"
                      :label="$t('messages.ordered')+':'"
                      label-for="ordered"
                      :invalid-feedback="$t('errors.ordered')">
          <b-form-input id="ordered" name="ordered"
                        min="0" :number="true" type="number"
                        v-model="product.ordered"
                        @input="$v.product.ordered.$touch"
                        :state="$v.product.ordered.$dirty ? !$v.product.ordered.$error : null"></b-form-input>
        </b-form-group>
        <div class="d-flex">
          <b-form-group id="preorderGroup"
                        v-if="editing"
                        :label="$t('messages.preorder')+':'"
                        label-for="preorder" class="d-flex mr-2">
            <b-form-checkbox id="preorder" name="preorder" class="product-checkbox-fix"
                             v-model="product.preorder"></b-form-checkbox>
          </b-form-group>
          <b-form-group id="backorderGroup"
                        v-if="editing"
                        :label="$t('messages.backorder')+':'"
                        label-for="backorder" class="d-flex">
            <b-form-checkbox id="backorder" name="backorder" class="product-checkbox-fix"
                             v-model="product.backorder"></b-form-checkbox>
          </b-form-group>
        </div>
        <b-form-group id="stockGroup"
                      v-if="editing && !product.preorder && !product.backorder"
                      :label="$t('messages.stock')+':'"
                      label-for="stock"
                      :invalid-feedback="$t('errors.stock')">
          <b-form-input id="stock" name="stock"
                        min="0" :number="true" type="number"
                        v-model="product.stock"
                        @input="$v.product.stock.$touch"
                        :state="$v.product.stock.$dirty ? !$v.product.stock.$error : null"></b-form-input>
        </b-form-group>
        <div class="box-icon d-flex" :class="{ 'mt-2': !editing }" v-if="product.preorder || product.backorder">
          <div class="box-icon-wrapper" :class="editing ? 'calendar-position-fix' : ''">
            <font-awesome-icon class="green" icon="calendar-alt"></font-awesome-icon>
          </div>
          <span v-if="!editing" class="month-position-fix strong green">{{ translateMonth(product.expected_date) }}</span>
          <span v-if="editing" class="d-flex ml-1">
            <date-picker id="expectedDatePicker" name="expectedDatePicker"
                         v-model="expectedDate" :config="expectedDateOptions" class="month-date-picker-width"
                         @input="$v.expectedDate.$touch"></date-picker>
          </span>
        </div>
      </div>
      <div class="card__bottom d-flex justify-content-between pt-0 mt-3" v-if="editing">
        <b-form @submit.prevent="saveProduct">
          <notification-button :message="$t('messages.edit')" :notification="notifications.product" field="product"
                               :disabled="$v.$invalid"></notification-button>
        </b-form>
      </div>
      <div class="mt-3" v-if="product.url"></div>
      <div class="card__url bg-primary" v-if="product.url"><a :href="product.url" target="_blank">{{ ((product.url.indexOf('boardgamegeek.com') >= 0) ? $t('messages.check_product_bgg') : $t('messages.check_product')) }}</a></div>
    </div>
  </div>
</template>

<script>
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import DatePicker from 'vue-bootstrap-datetimepicker'

import { productService } from '../../services/product.service'
import { daysService } from '../../services/days.service'
import NotificationButton from '../NotificationButton'
import CartButton from '../CartButton'

import { defaultMixin } from '../../mixin/default'
import { productMixin } from '../../mixin/product'
import { notificationMixin } from '../../mixin/notification'
import { validationMixin } from 'vuelidate'

import { required, requiredIf, decimal, minValue } from 'vuelidate/lib/validators'

import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css'

const decimalTwoRounds = (value) => {
  return value !== null && /^[-]?\d*(\.\d{0,2})?$/.test(value)
}

export default {
  name: 'productinfo',
  mixins: [ defaultMixin, productMixin, notificationMixin, validationMixin ],
  components: { NotificationButton, CartButton, DatePicker },
  props: {
    value: Object,
    editing: Boolean
  },
  data: function () {
    return {
      product: this.value,
      languages: productService.getLanguages(),
      editor: {
        instance: ClassicEditor,
        data: '',
        config: {
          toolbar: [ 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'insertTable', 'undo', 'redo' ],
          language: this.$i18n.locale
        },
        update: 0
      },
      expectedDate: null,
      expectedDateOptions: {
        format: 'MM-YYYY',
        locale: this.$i18n.locale
      },
      nextDeliveryDay: null,
      total: 1,
      notifications: {
        product: null
      }
    }
  },
  computed: {
    showOrderFast: function () {
      let date = new Date()
      let differenceTime = this.nextDeliveryDay.getTime() - date.getTime()
      let differenceDays = differenceTime / (1000 * 3600 * 24)
      return differenceDays < 1
    }
  },
  validations: function () {
    return {
      product: {
        price: {
          required,
          decimal,
          decimalTwoRounds,
          minValue: minValue(0)
        },
        reduced_price: {
          required,
          decimal,
          decimalTwoRounds,
          minValue: minValue(0)
        },
        ordered: {
          required,
          minValue: minValue(0)
        },
        stock: {
          required: requiredIf(function (value) {
            return !this.product.preorder && !this.product.backorder
          }),
          minValue: minValue(0)
        }
      },
      expectedDate: {
        required: requiredIf(function (value) {
          return this.product.preorder || this.product.backorder
        })
      }
    }
  },
  methods: {
    getFlagLanguageString: function (locale) {
      if (locale === 'en') {
        return 'gb'
      }
      return locale
    },
    saveProduct: function () {
      productService.updateProduct(this.product.id,
        { 'language': this.product.language,
          'ean': this.product.ean,
          'description': this.editor.data,
          'preorder': this.product.preorder,
          'backorder': this.product.backorder,
          'expectedDate': this.expectedDate,
          'price': this.product.price,
          'reducedPrice': this.product.reduced_price,
          'ordered': this.product.ordered,
          'stock': (this.product.preorder || this.product.backorder) ? null : this.product.stock,
          'productRange': this.product.product_range })
        .then(() => {
          this.product.description = this.editor.data
          this.product.expected_date = this.expectedDate

          this.$store.commit('success', 'success.product_edit')
          this.sendNotification('product', 'success')
        }, () => {
          this.$store.commit('error', 'errors.product_edit')
          this.sendNotification('product', 'error')
        })
    }
  },
  watch: {
    'product.preorder': function () {
      if (!this.product.preorder && !this.product.backorder) {
        this.expectedDate = null
      }
      if (this.product.preorder) {
        this.product.backorder = false
      }
    },
    'product.backorder': function () {
      if (!this.product.preorder && !this.product.backorder) {
        this.expectedDate = null
      }
      if (this.product.backorder) {
        this.product.preorder = false
      }
    }
  },
  created: function () {
    let self = this

    this.editor.data = this.product.description
    this.expectedDate = this.product.expected_date
    daysService.getNextPostNLDeliveryDay()
      .then((date) => {
        this.nextDeliveryDay = date
      })

    this.$root.$on('updateLocale', () => {
      self.expectedDateOptions.locale = self.$i18n.locale
      self.editor.config.language = self.$i18n.locale
      self.editor.update += 1
    })
    this.$root.$on('resetNotification', (notification) => {
      self.resetNotification(notification)
    })
  },
  beforeDestroy: function () {
    this.$root.$off('updateLocale')
    this.$root.$off('resetNotification')
  }
}
</script>

<style scoped>
  .flag-size {
    font-size: 18px;
  }

  .card__language {
    padding: 0px 20px 10px;
  }

  .card__url {
    padding: 20px;
    border-bottom: 1px #D3D3D3 solid;
  }

  .card__url a {
    color: #fff;
    font-weight: bold;
  }

  .product-range-checkbox-fix {
    margin-top: -15px;
    margin-bottom: 25px;
  }

  .product-checkbox-fix {
    margin-left: 10px;
    margin-top: -16px;
  }

  .calendar-position-fix {
    padding-top: 5px;
  }

  .month-position-fix {
    padding-left: 5px;
    padding-top: 1px;
  }

  .month-date-picker-width {
    width: 150px;
  }

  .total-width {
    width: 60px;
  }

  .position-button {
    position: absolute;
    top: 4px;
    left: 5px;
  }
</style>



// WEBPACK FOOTER //
// src/components/product/ProductInfo.vue